// Phase 7 Inbox — 3 columns: conversation list / thread / context tabs.
// Live socket updates patch the react-query caches in place so the UI
// flips without a refetch round-trip.

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, BotOff, Inbox as InboxIcon, Send, UserCog } from 'lucide-react';
import type {
  AiTrace,
  ConversationDetail,
  ConversationListItem,
  MessageItem,
} from '@qualibot/shared';
import { Card } from '@/components/primitives/Card';
import { Badge } from '@/components/primitives/Badge';
import { StatusBadge } from '@/components/primitives/StatusBadge';
import { Btn } from '@/components/primitives/Btn';
import { Empty } from '@/components/primitives/Empty';
import { Tabs } from '@/components/primitives/Tabs';
import {
  getConversation,
  getConversationTrace,
  listConversations,
  listMessages,
  sendConversationMessage,
  setConversationBot,
  takeOverConversation,
} from '@/lib/conversations-api';
import { useSocketEvent } from '@/lib/socket';
import { extractApiError } from '@/lib/api-client';
import { humanizeEnum } from '@/lib/format';

function formatTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString();
}

export function InboxPage() {
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const conversations = useQuery({
    queryKey: ['conversations'],
    queryFn: listConversations,
  });

  const activeId = routeId ?? conversations.data?.[0]?.id ?? null;

  // Keep URL in sync when we auto-pick the first conversation.
  useEffect(() => {
    if (!routeId && activeId) navigate(`/inbox/${activeId}`, { replace: true });
  }, [routeId, activeId, navigate]);

  const conversation = useQuery({
    queryKey: ['conversation', activeId],
    queryFn: () => getConversation(activeId!),
    enabled: !!activeId,
  });

  const messages = useQuery({
    queryKey: ['messages', activeId],
    queryFn: () => listMessages(activeId!),
    enabled: !!activeId,
  });

  // ----- Live updates -----
  useSocketEvent<{ conversationId: string; message: MessageItem }>(
    'whatsapp:message:new',
    (payload) => {
      qc.setQueryData<MessageItem[] | undefined>(
        ['messages', payload.conversationId],
        (prev) => {
          if (!prev) return prev;
          if (prev.some((m) => m.id === payload.message.id)) return prev;
          return [...prev, payload.message];
        },
      );
      qc.setQueryData<ConversationListItem[] | undefined>(['conversations'], (prev) => {
        if (!prev) return prev;
        const next = prev.map((c) =>
          c.id === payload.conversationId
            ? {
                ...c,
                lastMessagePreview: payload.message.text?.slice(0, 120) ?? null,
                lastMessageAt: payload.message.createdAt,
              }
            : c,
        );
        next.sort((a, b) => (b.lastMessageAt ?? '').localeCompare(a.lastMessageAt ?? ''));
        return next;
      });
    },
  );
  useSocketEvent<{ conversationId: string }>('conversation:updated', (payload) => {
    qc.invalidateQueries({ queryKey: ['conversation', payload.conversationId] });
    qc.invalidateQueries({ queryKey: ['conversations'] });
  });

  // ----- Composer -----
  const [draft, setDraft] = useState('');
  const [sendError, setSendError] = useState<string | null>(null);
  const sendMut = useMutation({
    mutationFn: (text: string) => sendConversationMessage(activeId!, { text }),
    onSuccess: () => setDraft(''),
    onError: (err) => setSendError(extractApiError(err)),
  });

  function onSend(e: FormEvent) {
    e.preventDefault();
    setSendError(null);
    const text = draft.trim();
    if (!text || !activeId) return;
    sendMut.mutate(text);
  }

  // ----- Actions -----
  const takeoverMut = useMutation({
    mutationFn: () => takeOverConversation(activeId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conversation', activeId] });
      qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
  const botMut = useMutation({
    mutationFn: (enabled: boolean) => setConversationBot(activeId!, enabled),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conversation', activeId] });
      qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });

  // Auto-scroll the thread on new messages.
  const threadRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    threadRef.current?.scrollTo({
      top: threadRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages.data?.length]);

  const [contextTab, setContextTab] = useState('context');

  const conv = conversation.data;
  const items = conversations.data ?? [];

  return (
    <div
      className="grid h-full min-h-0"
      style={{ gridTemplateColumns: '270px 1fr 320px' }}
    >
      {/* ---- LIST ---- */}
      <aside className="flex min-h-0 flex-col border-r border-border bg-surface">
        <div className="border-b border-border p-4">
          <div className="flex items-center justify-between">
            <span className="text-[15px] font-semibold">Inbox</span>
            <Badge tone="ok">Live</Badge>
          </div>
          <input
            className="mt-2 h-9 w-full rounded-md border border-border-strong bg-surface px-2.5 text-[13px] outline-none focus:border-accent"
            placeholder="Search conversations…"
            disabled
          />
        </div>
        <div className="flex-1 overflow-y-auto">
          {conversations.isLoading ? (
            <div className="py-8 text-center text-[13px] text-text-3">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-2">
              <Empty
                title="No conversations yet"
                body="Pair a WhatsApp number and message it from your phone."
                icon={InboxIcon}
              />
            </div>
          ) : (
            items.map((c) => {
              const active = c.id === activeId;
              return (
                <button
                  key={c.id}
                  onClick={() => navigate(`/inbox/${c.id}`)}
                  className="block w-full border-t border-border px-4 py-3 text-left transition hover:bg-surface-2"
                  style={{
                    background: active ? 'var(--accent-softer)' : undefined,
                    borderLeft: active
                      ? '3px solid var(--accent)'
                      : '3px solid transparent',
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[13px] font-semibold">{c.displayName}</span>
                    <span className="whitespace-nowrap text-[11px] text-text-3">
                      {formatTime(c.lastMessageAt)}
                    </span>
                  </div>
                  <div className="truncate text-[12px] text-text-2">
                    {c.lastMessagePreview ?? c.topic ?? '—'}
                  </div>
                  <div className="mt-0.5 text-[11px] text-text-3">
                    {c.agentName ?? 'Unassigned'} ·{' '}
                    {c.channelType ? humanizeEnum(c.channelType) : '—'}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </aside>

      {/* ---- THREAD ---- */}
      <section className="flex min-h-0 flex-col">
        {conv ? (
          <>
            <header className="flex items-center justify-between border-b border-border bg-surface px-5 py-3">
              <div>
                <div className="text-[14px] font-semibold">{conv.displayName}</div>
                <div className="text-[11.5px] text-text-3">
                  {conv.channelType ? humanizeEnum(conv.channelType) : '—'} ·{' '}
                  {conv.agentName ?? 'No agent'} · {humanizeEnum(conv.status)}
                  {conv.assignedToUserName ? ` · ${conv.assignedToUserName}` : ''}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Btn
                  size="sm"
                  icon={UserCog}
                  onClick={() => takeoverMut.mutate()}
                  disabled={takeoverMut.isPending}
                >
                  Take over
                </Btn>
                {conv.botEnabled ? (
                  <Btn
                    size="sm"
                    kind="ghost"
                    icon={BotOff}
                    onClick={() => botMut.mutate(false)}
                    disabled={botMut.isPending}
                  >
                    Disable bot
                  </Btn>
                ) : (
                  <Btn
                    size="sm"
                    kind="ghost"
                    icon={Bot}
                    onClick={() => botMut.mutate(true)}
                    disabled={botMut.isPending}
                  >
                    Enable bot
                  </Btn>
                )}
              </div>
            </header>

            <div ref={threadRef} className="flex-1 space-y-2 overflow-y-auto px-6 py-4">
              {messages.isLoading ? (
                <div className="text-center text-[13px] text-text-3">Loading messages…</div>
              ) : (messages.data ?? []).length === 0 ? (
                <div className="text-center text-[13px] text-text-3">No messages yet.</div>
              ) : (
                (messages.data ?? []).map((m) => {
                  const inbound = m.direction === 'INBOUND';
                  return (
                    <div
                      key={m.id}
                      className="max-w-[70%] rounded-lg px-3 py-2 text-[13.5px]"
                      style={{
                        background: inbound ? 'var(--surface)' : 'var(--accent)',
                        color: inbound ? 'var(--text)' : 'var(--accent-text)',
                        border: inbound ? '1px solid var(--border)' : '1px solid var(--accent)',
                        marginLeft: inbound ? 0 : 'auto',
                      }}
                    >
                      {m.mediaFileName ? (
                        <div
                          className="mb-1 rounded px-2 py-1 text-[11.5px]"
                          style={{
                            background: 'var(--surface-2)',
                            color: 'var(--text-2)',
                            fontFamily: 'var(--font-mono)',
                            border: '1px solid var(--border)',
                          }}
                        >
                          {m.mediaFileName}
                        </div>
                      ) : null}
                      {m.text}
                      <div
                        className="mt-1 text-[11px]"
                        style={{
                          color: inbound ? 'var(--text-3)' : 'rgba(255,255,255,0.75)',
                        }}
                      >
                        {humanizeEnum(m.senderType)} · {formatTime(m.createdAt)}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <form
              onSubmit={onSend}
              className="flex flex-col gap-1 border-t border-border bg-surface px-5 py-3"
            >
              {sendError ? (
                <div
                  role="alert"
                  className="rounded-md px-3 py-2 text-[12.5px]"
                  style={{ background: 'var(--err-soft)', color: 'var(--err)' }}
                >
                  {sendError}
                </div>
              ) : null}
              <div className="flex items-center gap-2">
                <input
                  className="h-10 flex-1 rounded-[10px] border border-border-strong bg-surface px-3 text-[13.5px] outline-none focus:border-accent"
                  placeholder="Type a message as the human operator…"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  disabled={sendMut.isPending}
                />
                <Btn
                  type="submit"
                  kind="primary"
                  icon={Send}
                  disabled={!draft.trim() || sendMut.isPending}
                >
                  {sendMut.isPending ? 'Sending…' : 'Send'}
                </Btn>
              </div>
            </form>
          </>
        ) : (
          <div className="flex flex-1 items-center justify-center text-[13px] text-text-3">
            {conversations.isLoading ? '' : 'Select a conversation'}
          </div>
        )}
      </section>

      {/* ---- CONTEXT ---- */}
      <aside className="overflow-y-auto border-l border-border bg-surface">
        {conv ? (
          <>
            <div className="px-4 pt-4">
              <Tabs
                tabs={[
                  { id: 'context', label: 'Context' },
                  { id: 'trace', label: 'AI Trace' },
                  { id: 'audit', label: 'Audit' },
                ]}
                active={contextTab}
                onChange={setContextTab}
              />
            </div>
            <div className="px-4 pb-5">
              {contextTab === 'context' ? (
                <ContextPanel conv={conv} />
              ) : contextTab === 'trace' ? (
                <TracePanel conversationId={conv.id} />
              ) : (
                <Card>
                  <div className="text-[13px] text-text-3">
                    Per-conversation audit feed lands in Phase 10.
                  </div>
                </Card>
              )}
            </div>
          </>
        ) : null}
      </aside>
    </div>
  );
}

function ContextPanel({ conv }: { conv: ConversationDetail }) {
  const rows: [string, string][] = [
    ['Agent', conv.agentName ?? '—'],
    ['Channel', conv.channelType ? humanizeEnum(conv.channelType) : '—'],
    ['User type', conv.userType ?? '—'],
    ['Verified', conv.verified ? 'Yes' : 'No'],
    ['Status', humanizeEnum(conv.status)],
    ['Bot', conv.botEnabled ? 'On' : 'Off'],
    ['Assignee', conv.assignedToUserName ?? 'Unassigned'],
    ['JID', conv.externalConversationId ?? '—'],
  ];
  return (
    <div>
      {rows.map(([k, v], i) => (
        <div
          key={k}
          className="flex items-center justify-between py-2 text-[13px]"
          style={{ borderTop: i ? '1px solid var(--border)' : 'none' }}
        >
          <span className="text-text-3">{k}</span>
          <span
            className="font-medium"
            style={k === 'JID' ? { fontFamily: 'var(--font-mono)', fontSize: 11.5 } : undefined}
          >
            {v}
          </span>
        </div>
      ))}
    </div>
  );
}

function TracePanel({ conversationId }: { conversationId: string }) {
  const trace = useQuery<AiTrace>({
    queryKey: ['trace', conversationId],
    queryFn: () => getConversationTrace(conversationId),
    refetchInterval: 8000,
  });

  // Patch the cache live when a new message lands so trace updates without
  // waiting for the 8s poll.
  const qc = useQueryClient();
  useSocketEvent<{ conversationId: string }>('conversation:updated', (payload) => {
    if (payload.conversationId === conversationId) {
      qc.invalidateQueries({ queryKey: ['trace', conversationId] });
    }
  });

  if (trace.isLoading) {
    return (
      <Card>
        <div className="text-[13px] text-text-3">Loading trace…</div>
      </Card>
    );
  }
  const t = trace.data;
  if (!t) return null;

  const traceStatusFor = (s: string) =>
    s === 'SUCCESS' ? 'Connected' : s === 'FAILED' ? 'Disconnected' : s === 'PENDING' ? 'Warning' : 'Available';

  return (
    <div className="flex flex-col gap-3.5">
      {t.degradedFromCap && (
        <div
          role="alert"
          className="rounded-md px-3 py-2 text-[12px]"
          style={{ background: 'var(--warn-soft)', color: 'var(--warn)' }}
        >
          Daily LLM budget reached for this tenant — bot is running in degraded mode. Replies use canned templates until the cap resets at 00:00 UTC.
        </div>
      )}

      <div>
        <div className="text-[11.5px] text-text-3 mb-1">Detected intent</div>
        <div className="flex items-center gap-2">
          <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
            {t.intent ?? '—'}
          </span>
          {t.confidence ? <Badge tone="ok">{t.confidence}</Badge> : null}
        </div>
      </div>

      <div>
        <div className="text-[11.5px] text-text-3 mb-1">Specialist / skill</div>
        <div className="text-[13px] font-medium">{t.specialist ?? '—'}</div>
        {t.skillUsed ? (
          <div className="text-[12px] text-text-3 mt-0.5">Last skill: {t.skillUsed}</div>
        ) : null}
      </div>

      {t.extractedFields.length > 0 && (
        <div>
          <div className="text-[11.5px] text-text-3 mb-1">Extracted fields</div>
          <Card pad={0}>
            {t.extractedFields.map((f, i) => (
              <div
                key={f.key}
                className="flex items-center justify-between px-3 py-1.5 text-[12.5px]"
                style={{ borderTop: i ? '1px solid var(--border)' : 'none' }}
              >
                <span className="text-text-3">{f.key}</span>
                <span className="font-medium">{f.value}</span>
              </div>
            ))}
          </Card>
        </div>
      )}

      <div>
        <div className="text-[11.5px] text-text-3 mb-1">Tool calls</div>
        {t.toolCalls.length === 0 ? (
          <div className="text-[12px] text-text-3">No tool calls yet.</div>
        ) : (
          <div className="flex flex-col gap-1.5">
            {t.toolCalls.map((c, i) => (
              <Card key={i} pad={0}>
                <div className="flex items-center justify-between px-3 py-2">
                  <div>
                    <div className="text-[11.5px]" style={{ fontFamily: 'var(--font-mono)' }}>
                      {c.toolName}
                    </div>
                    <div className="text-[11px] text-text-3 mt-0.5">
                      {c.latencyMs != null ? `${c.latencyMs}ms` : 'pending'}
                    </div>
                  </div>
                  <StatusBadge status={traceStatusFor(c.status)} />
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {t.guardrails.length > 0 && (
        <div>
          <div className="text-[11.5px] text-text-3 mb-1">Guardrails</div>
          <Card>
            <ul className="text-[12.5px] leading-relaxed list-disc pl-5">
              {t.guardrails.map((g) => (
                <li key={g}>{g}</li>
              ))}
            </ul>
          </Card>
        </div>
      )}
    </div>
  );
}
